import React from 'react'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { DocumentCheckCache } from '@pages/enroll/document-check'

type BiographicAuthInfo = {
  phoneNumber: string
  emailAddress: string
}

type ApptInfo = {
  convertedStartTime?: string
  locationAddress?: string
  documentAcronym?: string
  photoIdAcronym?: string
  firstName?: string
  lastName?: string
  applicationId?: string
  middleName?: string
  locationId?: string
  dateOfBirth?: Date
  enrollmentLocationId?: string
  email?: string
  enrollmentId?: string
}

export type EnrollmentContext = {
  state: Enrollment.Enrollment
  selectedPlan?: string | null
  affiliate?: string | null
  isPlanBundled: boolean | null
  isVoucherApplied: boolean | null
  ambassadorSecurityLoginActive?: boolean
  documentCheckCache?: DocumentCheckCache
  clearState: () => void
  updateState: (newState: Enrollment.Enrollment) => void
  updateDocumentCheckCache: (newCache: DocumentCheckCache) => void
  setSelectedPlan: (plan: string) => void
  setIsPlanBundled: (arg: boolean) => void
  setIsVoucherApplied: (arg: boolean) => void
  setAffiliate: (affiliate: string) => void
  setAmbassadorSecurityLoginActive: (
    ambassadorSecurityLoginActive: boolean
  ) => void

  biographicAuthInfo: BiographicAuthInfo
  setBiographicAuthInfo: (args: BiographicAuthInfo) => void

  apptInfo: ApptInfo
  setApptInfo: (args: ApptInfo) => void

  voucher: Enrollment.Voucher | undefined
  setVoucher: (voucher: Enrollment.Voucher | undefined) => void
}

export enum PlanTypes {
  ENROLL = 'tpc-enroll-5-plan',
  RENEW = 'tpc-renew-5-online-plan',
}

const defaultInitialState: EnrollmentContext = {
  state: {
    id: '',
    applicationId: '',
  },
  selectedPlan: null,
  isPlanBundled: null,
  isVoucherApplied: false,
  ambassadorSecurityLoginActive: false,
  affiliate: null,
  setBiographicAuthInfo: () => null,
  biographicAuthInfo: null as never,
  clearState: () => null,
  updateState: () => null,
  updateDocumentCheckCache: () => null,
  setSelectedPlan: () => null,
  setAffiliate: () => null,
  setAmbassadorSecurityLoginActive: () => false,
  setIsPlanBundled: () => null,
  setIsVoucherApplied: () => null,

  apptInfo: null as never,
  setApptInfo: () => null,

  voucher: undefined,
  setVoucher: () => undefined,
}

const EnrollmentStateContext =
  React.createContext<EnrollmentContext>(defaultInitialState)

function EnrollmentProvider({
  children,
  initialState = defaultInitialState,
}: {
  children: React.ReactNode
  initialState?: EnrollmentContext
}) {
  const [enrollmentState, setEnrollmentState] = React.useState(
    initialState.state
  )
  const [documentCheckCache, setDocumentCheckCache] = React.useState(
    initialState.documentCheckCache
  )

  const [ambassadorSecurityLoginActive, setAmbassadorSecurityLoginActive] =
    React.useState(initialState.ambassadorSecurityLoginActive)

  const [selectedPlan, setSelectedPlan] = React.useState(
    initialState.selectedPlan
  )
  const [isPlanBundled, setIsPlanBundled] = React.useState(
    initialState.isPlanBundled
  )
  const [isVoucherApplied, setIsVoucherApplied] = React.useState(
    initialState.isVoucherApplied
  )

  const [affiliate, setAffiliate] = React.useState(initialState.affiliate)
  const [biographicAuthInfo, setBiographicAuthInfo] = React.useState(
    initialState.biographicAuthInfo
  )
  const [apptInfo, setApptInfo] = React.useState(initialState.apptInfo)

  const [voucher, setVoucher] = React.useState(initialState.voucher)

  function clearState() {
    setEnrollmentState(initialState.state)
    setDocumentCheckCache(initialState.documentCheckCache)
    setSelectedPlan(initialState.selectedPlan)
    setAffiliate(initialState.affiliate)
    setAmbassadorSecurityLoginActive(initialState.ambassadorSecurityLoginActive)
    setBiographicAuthInfo(initialState.biographicAuthInfo)
    setVoucher(initialState.voucher)
  }

  return (
    <EnrollmentStateContext.Provider
      value={{
        state: enrollmentState,
        documentCheckCache,
        selectedPlan,
        updateState: setEnrollmentState,
        updateDocumentCheckCache: setDocumentCheckCache,
        clearState,
        setSelectedPlan,
        setAffiliate,
        affiliate,
        setAmbassadorSecurityLoginActive,
        ambassadorSecurityLoginActive,
        isPlanBundled,
        setIsPlanBundled,
        isVoucherApplied,
        setIsVoucherApplied,
        setBiographicAuthInfo,
        biographicAuthInfo,
        apptInfo,
        setApptInfo,

        voucher,
        setVoucher,
      }}
    >
      {children}
    </EnrollmentStateContext.Provider>
  )
}

function useEnrollmentContext() {
  const context = React.useContext(EnrollmentStateContext)
  if (context === undefined) {
    throw new Error(
      'useEnrollmentContext must be used within an EnrollmentProvider'
    )
  }
  return context
}

export { EnrollmentProvider, useEnrollmentContext }
